import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { T_KEYS } from '../../../constants';

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    maxWidth: '420px',
    width: '100%',
    padding: '0',
  },
});

const Header = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '20px 24px',
  borderBottom: '1px solid #f0f0f0',
});

const HeaderTitle = styled(Typography)({
  fontSize: '18px',
  fontWeight: 600,
  color: '#262626',
});

const Content = styled(Box)({
  padding: '24px',
  textAlign: 'center',
});

const IconContainer = styled(Box)({
  width: '56px',
  height: '56px',
  borderRadius: '50%',
  backgroundColor: '#fff3e0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 16px',
});

const Message = styled(Typography)({
  fontSize: '14px',
  color: '#666666',
  lineHeight: '22px',
});

const Actions = styled(DialogActions)({
  padding: '16px 24px 24px',
  gap: '12px',
});

const CancelButton = styled(Button)({
  flex: 1,
  padding: '12px 24px',
  borderRadius: '12px',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  color: '#666666',
  backgroundColor: '#f5f5f5',
  '&:hover': {
    backgroundColor: '#e8e8e8',
  },
});

const ConfirmButton = styled(Button)({
  flex: 1,
  padding: '12px 24px',
  borderRadius: '12px',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  color: '#ffffff',
  backgroundColor: '#ff6b6b',
  '&:hover': {
    backgroundColor: '#ff5252',
  },
});

interface UnmapConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
}

const UnmapConfirmModal: React.FC<UnmapConfirmModalProps> = memo(({
  open,
  onClose,
  onConfirm,
  isLoading,
}) => {
  const { t } = useTranslation();

  return (
    <StyledDialog open={open} onClose={onClose}>
      <Header>
        <HeaderTitle>
          {t(T_KEYS.UNITS_MAPPING_UNMAP_CONFIRM_TITLE)}
        </HeaderTitle>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ color: '#666666' }} />
        </IconButton>
      </Header>

      <Content>
        <IconContainer>
          <WarningAmberIcon sx={{ fontSize: 28, color: '#ff9800' }} />
        </IconContainer>

        <Message>
          {t(T_KEYS.UNITS_MAPPING_UNMAP_CONFIRM_MESSAGE)}
        </Message>
      </Content>

      <Actions>
        <CancelButton onClick={onClose} disabled={isLoading}>
          {t(T_KEYS.UNITS_MAPPING_UNMAP_CONFIRM_NO)}
        </CancelButton>
        <ConfirmButton onClick={onConfirm} disabled={isLoading}>
          {isLoading 
            ? t(T_KEYS.COMMON_LOADING)
            : t(T_KEYS.UNITS_MAPPING_UNMAP_CONFIRM_YES)
          }
        </ConfirmButton>
      </Actions>
    </StyledDialog>
  );
});

UnmapConfirmModal.displayName = 'UnmapConfirmModal';

export default UnmapConfirmModal;
